import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import useVoiceSocket from './useVoiceSocket';
import useVoiceSipStatus from './useVoiceSipStatus';
import useVoiceWebRTC from './useVoiceWebRTC';
import {
  voiceSetConnected,
  voiceSetMuted,
  voiceReleaseCallSession,
  voiceSetError
} from '../../../../actions/voice/voiceActions';
import {
  releaseAgentCallOnSdkTerminal,
  releaseAgentLocalAudioCapture,
  subscribeAgentSdkEvents,
  prefetchAgentLocalAudioStream,
  unlockAgentRemoteAudio,
  hangupCurrentAgentCall
} from '../infrastructure/webrtc/voiceAgentWebRtcAdapter';
import { isTerminalSessionState } from '../infrastructure/webrtc/voiceJsSipSessionStatus';
import {
  completeVoiceCallHangup,
  isVoiceHangupInFlight,
  isVoiceHangupPosted,
  isVoiceAnswerBridgeInFlight,
  markPendingMediaFailedHangup,
  resolveEndedReasonForVoiceCall,
  getVoiceTabClaimedCallControlId
} from './voiceHangupCoordinator';
import { VOICE_WEBRTC_REMOTE_MEDIA_ELEMENT_ID } from '../infrastructure/config/voiceWebRtcConfig';
import { VOICE_ANSWER_ERROR, classifyVoiceAnswerError } from '../domain/voiceAnswerErrors';
import { VOICE_ENDED_REASON_HANGUP } from '../domain/voiceHangupEndedReason';
import { voiceSocketLog } from '../../../utils/voiceSocketDebug';

/**
 * Voice Platform lifecycle (socket always; JsSIP/WebRTC starts when this runtime mounts).
 *
 * WebRTC connect timing:
 * - Mount: register SIP/WebRTC as soon as `VoiceRuntimeBoot` mounts.
 * - Unexpected SIP disconnect: adapter unregisters/stops UA, rotates tab_session_id when dirty,
 *   and reconnects with backoff (one contact per agent_<tabId>).
 * - Incoming/outbound handlers and `needsWebRtcSession` remain as backup/reconnect paths.
 * - Unmount: disconnect JsSIP so a remount gets a fresh registration.
 * - First user gesture in this tab: unlock remote audio + prefetch mic (avoid ringtone-only unlock).
 */
export default function useVoiceRuntime() {
  const dispatch = useDispatch();
  const { t } = useTranslation('Voice');
  const activeCall = useSelector((state) => state.voiceReducer?.activeCall);
  const outboundDialingCall = useSelector((state) => state.voiceReducer?.outboundDialingCall);
  const incomingCalls = useSelector((state) => state.voiceReducer?.incomingCalls ?? []);
  const isRinging = useSelector((state) => state.voiceReducer?.isRinging ?? false);
  const activeCallRef = useRef(activeCall);
  activeCallRef.current = activeCall;
  const hangupStateRef = useRef({
    activeCallControlId: activeCall?.callControlId ?? null,
    outboundDialingCallControlId: outboundDialingCall?.callControlId ?? null,
    hasIncomingCalls: incomingCalls.length > 0
  });
  hangupStateRef.current = {
    activeCallControlId: activeCall?.callControlId ?? null,
    outboundDialingCallControlId: outboundDialingCall?.callControlId ?? null,
    hasIncomingCalls: incomingCalls.length > 0
  };
  const terminalCleanupForCallControlIdRef = useRef(null);

  const { isVoiceSipEnabled } = useVoiceSipStatus();
  const isVoiceSipEnabledRef = useRef(isVoiceSipEnabled);
  isVoiceSipEnabledRef.current = isVoiceSipEnabled;

  const { isSocketConnected } = useVoiceSocket({ enabled: isVoiceSipEnabled });

  const {
    connect: connectWebRtc,
    disconnect: disconnectWebRtc,
    isConnected: isWebRtcConnected,
    isMuted: isWebRtcMuted
  } = useVoiceWebRTC();
  const webRtcConnectInFlightRef = useRef(null);

  const needsWebRtcSession = useMemo(() => (
    Boolean(
      activeCall
      || outboundDialingCall
      || incomingCalls.length > 0
      || isRinging
    )
  ), [activeCall, outboundDialingCall, incomingCalls.length, isRinging]);

  const ensureWebRtcConnected = React.useCallback((reason) => {
    if (!isVoiceSipEnabledRef.current) {
      voiceSocketLog('webrtc connect skipped (sip disabled)', { reason });
      return Promise.resolve();
    }
    if (isWebRtcConnected) return Promise.resolve();
    if (webRtcConnectInFlightRef.current) {
      return webRtcConnectInFlightRef.current;
    }
    const runPromise = connectWebRtc()
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.warn(
          '[voice-webrtc] Auto-connect failed (will retry):',
          {
            reason,
            message: error?.message ?? error
          }
        );
      })
      .finally(() => {
        webRtcConnectInFlightRef.current = null;
      });
    webRtcConnectInFlightRef.current = runPromise;
    return runPromise;
  }, [connectWebRtc, isWebRtcConnected]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const hasLiveCallSession = Boolean(
          hangupStateRef.current.activeCallControlId
          || hangupStateRef.current.outboundDialingCallControlId
          || hangupStateRef.current.hasIncomingCalls
        );
        if (!hasLiveCallSession) {
          await disconnectWebRtc();
        }
        if (cancelled) return;
        if (!isVoiceSipEnabledRef.current) {
          voiceSocketLog('runtime-mount connect skipped (sip disabled)');
          return;
        }
        await connectWebRtc();
      } catch (error) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn(
          '[voice-webrtc] Mount connect failed (will retry):',
          {
            reason: 'runtime-mount',
            message: error?.message ?? error
          }
        );
      }
    })();

    return () => {
      cancelled = true;
      webRtcConnectInFlightRef.current = null;
      // Keep SIP/WebRTC + Redux call state if a live session exists (in-app navigation remounts).
      const hasLiveCallSession = Boolean(
        hangupStateRef.current.activeCallControlId
        || hangupStateRef.current.outboundDialingCallControlId
        || hangupStateRef.current.hasIncomingCalls
      );
      if (!hasLiveCallSession) {
        disconnectWebRtc().catch(() => {});
      }
    };
    // Connect once per VoiceRuntimeBoot mount; reconnect paths use ensureWebRtcConnected.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const warmAudioFromUserGesture = () => {
      unlockAgentRemoteAudio();
      prefetchAgentLocalAudioStream().catch(() => {});
    };

    window.addEventListener('pointerdown', warmAudioFromUserGesture, { once: true, capture: true });
    window.addEventListener('keydown', warmAudioFromUserGesture, { once: true, capture: true });

    return () => {
      window.removeEventListener('pointerdown', warmAudioFromUserGesture, { capture: true });
      window.removeEventListener('keydown', warmAudioFromUserGesture, { capture: true });
    };
  }, []);

  useEffect(() => {
    if (!needsWebRtcSession) return undefined;
    ensureWebRtcConnected('call-session-demand');
    return undefined;
  }, [needsWebRtcSession, ensureWebRtcConnected]);

  useEffect(() => {
    // Keep agent SIP registered while the inbox is open (not only during an active call).
    if (!isSocketConnected) return undefined;
    if (isWebRtcConnected) return undefined;
    ensureWebRtcConnected('socket-reconnected');
    return undefined;
  }, [
    ensureWebRtcConnected,
    isSocketConnected,
    isWebRtcConnected
  ]);

  useEffect(() => {
    const handleBrowserOnline = () => {
      if (isWebRtcConnected) return;
      ensureWebRtcConnected('browser-online');
    };
    window.addEventListener('online', handleBrowserOnline);
    return () => {
      window.removeEventListener('online', handleBrowserOnline);
    };
  }, [ensureWebRtcConnected, isWebRtcConnected]);

  useEffect(() => {
    dispatch(voiceSetConnected(isWebRtcConnected));
  }, [dispatch, isWebRtcConnected]);

  useEffect(() => {
    dispatch(voiceSetMuted(isWebRtcMuted));
  }, [dispatch, isWebRtcMuted]);

  useEffect(() => {
    const callControlId = activeCall?.callControlId;
    if (
      callControlId
      && terminalCleanupForCallControlIdRef.current
      && terminalCleanupForCallControlIdRef.current !== callControlId
    ) {
      terminalCleanupForCallControlIdRef.current = null;
    }
  }, [activeCall?.callControlId]);

  useEffect(() => {
    const unsubscribe = subscribeAgentSdkEvents((event) => {
      if (event.type !== 'ended' && event.type !== 'failed') return;
      if (!event.session) return;

      const sessionState = event.session.status;
      if (!isTerminalSessionState(sessionState)) return;

      const trackedCall = activeCallRef.current;
      const trackedCallControlId = trackedCall?.callControlId
        ?? getVoiceTabClaimedCallControlId();
      if (!trackedCallControlId) return;

      releaseAgentCallOnSdkTerminal(event.session);
      releaseAgentLocalAudioCapture();

      if (isVoiceHangupInFlight(trackedCallControlId) || isVoiceHangupPosted(trackedCallControlId)) {
        return;
      }

      // Contestar still open (POST /answer in flight): never race Nest with POST /hangup.
      if (isVoiceAnswerBridgeInFlight(trackedCallControlId)) {
        voiceSocketLog('sdk terminal ignored during answer bridge (no hangup race)', {
          callControlId: trackedCallControlId,
          eventType: event.type
        });
        return;
      }

      if (terminalCleanupForCallControlIdRef.current === trackedCallControlId) return;
      terminalCleanupForCallControlIdRef.current = trackedCallControlId;

      const endedReason = resolveEndedReasonForVoiceCall(hangupStateRef.current, trackedCallControlId);
      completeVoiceCallHangup({
        callControlId: trackedCallControlId,
        endedReason,
        onAfterHttpOk: () => {
          dispatch(voiceReleaseCallSession(trackedCallControlId));
          dispatch(voiceSetMuted(false));
        }
      }).catch((error) => {
        terminalCleanupForCallControlIdRef.current = null;
        dispatch(voiceSetError(
          error instanceof Error ? error.message : 'Failed to hang up call'
        ));
      });
    });

    return unsubscribe;
  }, [dispatch]);

  useEffect(() => {
    const unsubscribe = subscribeAgentSdkEvents((event) => {
      if (event.type !== 'mediaFailed') return;

      const trackedCallControlId = event.callControlId
        || activeCallRef.current?.callControlId
        || getVoiceTabClaimedCallControlId();

      dispatch(voiceSetError(
        t(`errors.${classifyVoiceAnswerError(event.error) || VOICE_ANSWER_ERROR.WEBRTC_MEDIA_FAILED}`, {
          defaultValue: 'Sin media WebRTC/TURN'
        })
      ));

      // Contestar still open: never hang up — Nest POST /answer would 502 and Chrome "corta al iniciar".
      if (trackedCallControlId && isVoiceAnswerBridgeInFlight(trackedCallControlId)) {
        markPendingMediaFailedHangup(trackedCallControlId);
        voiceSocketLog('mediaFailed deferred during answer bridge (no hangup race)', {
          callControlId: trackedCallControlId
        });
        return;
      }

      hangupCurrentAgentCall().catch(() => {});

      if (!trackedCallControlId) return;
      if (isVoiceHangupInFlight(trackedCallControlId) || isVoiceHangupPosted(trackedCallControlId)) {
        return;
      }
      if (terminalCleanupForCallControlIdRef.current === trackedCallControlId) return;
      terminalCleanupForCallControlIdRef.current = trackedCallControlId;

      completeVoiceCallHangup({
        callControlId: trackedCallControlId,
        endedReason: VOICE_ENDED_REASON_HANGUP,
        onAfterHttpOk: () => {
          dispatch(voiceReleaseCallSession(trackedCallControlId));
          dispatch(voiceSetMuted(false));
        }
      }).catch(() => {
        terminalCleanupForCallControlIdRef.current = null;
      });
    });

    return unsubscribe;
  }, [dispatch, t]);
}

/**
 * Boots Voice socket + JsSIP/WebRTC on mount (remote audio element for WebRTC playback).
 */
export const VoiceRuntimeBoot = () => {
  useVoiceRuntime();

  return (
    <audio
      id={VOICE_WEBRTC_REMOTE_MEDIA_ELEMENT_ID}
      autoPlay
      style={{
        position: 'fixed',
        left: 0,
        bottom: 0,
        width: '1px',
        height: '1px',
        opacity: 0.01,
        pointerEvents: 'none',
        zIndex: -1
      }}
      aria-hidden
    />
  );
};
